/**
 * COMPARISON MODE — a second place, over the same period.
 *
 * ── WHY A SECOND SCOPE AND NOT A SECOND PERIOD ──
 *
 * "vs previous period" already exists and is a different question. This one is
 * "Aurora Tower against Aurora Mews, both over the last 30 days" — the question
 * a manager with two buildings asks first and the one none of the three screens
 * this replaces could answer at all. They could each show ONE scope; comparing
 * meant two browser tabs and a subtraction done in your head.
 *
 * ── THE TWO COMPARISONS ARE MUTUALLY EXCLUSIVE, AND THAT IS ENFORCED HERE ──
 *
 * Turning this on sets `compare` to 'none'. With both on, every panel would
 * carry four series — this building now, this building then, that building now,
 * that building then — and every tile would carry two deltas answering different
 * questions. The reader would have to work out which arrow meant what, on the
 * screen whose entire argument is that they should not have to.
 *
 * It is also the request budget: comparison mode already doubles the analytics
 * requests against a pool of five, and period-over-period doubles the work
 * inside each one. The two multipliers never combine.
 *
 * ── AND IT IS OFFERED ONLY WHERE IT MEANS SOMETHING ──
 *
 * A caller who can see one building has nothing to compare it with. The control
 * is not rendered for them rather than rendered and disabled: a permanently
 * greyed control is a promise the product cannot keep.
 */
import React from 'react';
import { DashboardFilter, FilterMeta } from '../state/filter-model';
import { FilterAction } from '../state/filter-reducer';
import {
  BarSelect,
  Field,
  ToggleField,
  ToggleInput,
} from '../dashboard-screen.styled';

export interface CompareScopeProps {
  filter: DashboardFilter;
  meta?: FilterMeta;
  dispatch: (action: FilterAction) => void;
}

/** What to call a scope on a split tile. `null` on both sides means "everything
 *  this reader can see", which is a real scope and needs a real name. */
export const scopeName = (
  scope: { portfolioId: number | null; buildingId: number | null } | null,
  meta?: FilterMeta
): string => {
  if (!scope) return 'All buildings';
  if (scope.buildingId != null) {
    return (
      meta?.buildings.find(b => b.id === scope.buildingId)?.name ||
      `Building ${scope.buildingId}`
    );
  }
  if (scope.portfolioId != null) {
    return (
      meta?.portfolios.find(p => p.id === scope.portfolioId)?.name ||
      `Portfolio ${scope.portfolioId}`
    );
  }

  return 'All buildings';
};

/** The two names a comparing screen prints, in the order the tile prints them. */
export const compareLabels = (
  filter: DashboardFilter,
  meta?: FilterMeta
): { primary: string; compare: string } => ({
  primary: scopeName(
    { portfolioId: filter.portfolioId, buildingId: filter.buildingId },
    meta
  ),
  compare: scopeName(filter.compareScope, meta),
});

export const CompareScopePicker: React.FC<CompareScopeProps> = ({
  filter,
  meta,
  dispatch,
}) => {
  const buildings = meta?.buildings || [];
  const portfolios = meta?.portfolios || [];

  // Nothing to compare against. See the header: not rendered rather than
  // rendered-and-disabled.
  if (buildings.length < 2 && portfolios.length < 2) return null;

  const on = Boolean(filter.compareScope);

  const first = buildings.find(b => b.id !== filter.buildingId);

  return (
    <>
      <ToggleField>
        <ToggleInput
          type="checkbox"
          checked={on}
          data-testid="compare-scope-toggle"
          onChange={event => {
            if (!event.target.checked) {
              dispatch({ type: 'setCompareScope', scope: null });

              return;
            }
            // Turning it on with nothing chosen would leave an empty second
            // column on every tile. It opens on the first building that is not
            // already the primary.
            dispatch({
              type: 'setCompareScope',
              scope: { portfolioId: null, buildingId: first ? first.id : null },
            });
          }}
        />
        compare two places
      </ToggleField>

      {on ? (
        <Field>
          <span>against</span>
          <BarSelect
            data-testid="compare-scope-select"
            value={
              filter.compareScope?.buildingId != null
                ? `b${filter.compareScope.buildingId}`
                : filter.compareScope?.portfolioId != null
                ? `p${filter.compareScope.portfolioId}`
                : ''
            }
            onChange={event => {
              const raw = event.target.value;
              /* NO "all buildings" OPTION. The whole organisation INCLUDES the
                 primary building, so the difference between them double-counts
                 it — a comparison that is arithmetically valid and means
                 nothing. Both sides are always a named place. */
              dispatch({
                type: 'setCompareScope',
                scope: raw.startsWith('b')
                  ? { portfolioId: null, buildingId: Number(raw.slice(1)) }
                  : { portfolioId: Number(raw.slice(1)), buildingId: null },
              });
            }}
          >
            {portfolios.map(portfolio => (
              <option key={`p${portfolio.id}`} value={`p${portfolio.id}`}>
                {portfolio.name}
              </option>
            ))}
            {buildings.map(building => (
              <option key={`b${building.id}`} value={`b${building.id}`}>
                {building.name}
              </option>
            ))}
          </BarSelect>
        </Field>
      ) : null}
    </>
  );
};
